import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { GitMerge, Users, Truck, Car, Package, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const GRUPOS = [
  { key: "duplicados_clientes", entity: "cliente", label: "Clientes duplicados", icon: Users },
  { key: "duplicados_fornecedores", entity: "fornecedor", label: "Fornecedores duplicados", icon: Truck },
  { key: "duplicados_veiculos", entity: "veiculo", label: "Matrículas / veículos duplicados", icon: Car },
  { key: "duplicados_artigos", entity: "artigo", label: "Artigos / referências duplicados", icon: Package },
];

export default function Duplicados() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [dlg, setDlg] = useState(null); // { entity, itens }
  const [keepId, setKeepId] = useState("");

  const load = () => api.get("/copilot/sugestoes").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const openMerge = (entity, itens) => { setDlg({ entity, itens }); setKeepId(itens[0]?.id || ""); };

  const doMerge = async () => {
    if (!keepId) return;
    const others = dlg.itens.filter((i) => i.id !== keepId);
    try {
      for (const o of others) {
        await api.post("/merge", { entity: dlg.entity, source_id: o.id, target_id: keepId });
      }
      toast.success("Registos unidos. O histórico foi preservado.");
      setDlg(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-xl font-bold text-slate-900 mb-1 flex items-center gap-2"><GitMerge size={20} /> Duplicados & União</h1>
      <p className="text-sm text-slate-500 mb-4">O Copiloto sugere registos possivelmente duplicados. Você escolhe qual manter — os restantes são arquivados e todas as referências passam para o registo mantido. Nada é apagado.</p>

      {data && data.duplicados_total === 0 && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-10 text-center text-slate-500" data-testid="sem-duplicados">
          <CheckCircle2 className="mx-auto mb-2 text-emerald-400" size={32} />
          Sem duplicados detetados. Tudo limpo!
        </div>
      )}

      <div className="space-y-5">
        {data && GRUPOS.map((g) => {
          const grupos = data[g.key] || [];
          if (!grupos.length) return null;
          return (
            <div key={g.key}>
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><g.icon size={14} /> {g.label}</h3>
              <div className="space-y-2">
                {grupos.map((grp, idx) => (
                  <div key={idx} className="bg-white rounded-lg border border-amber-200 shadow-sm p-3" data-testid={`grupo-${g.entity}-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-700 space-y-0.5">
                        {grp.itens.map((it) => <div key={it.id} className="font-medium">• {it.label}</div>)}
                      </div>
                      {isAdmin && <Button size="sm" className="bg-violet-600 hover:bg-violet-700 gap-1.5 shrink-0" onClick={() => openMerge(g.entity, grp.itens)} data-testid={`btn-unir-${g.entity}-${idx}`}>
                        <GitMerge size={14} /> Unir
                      </Button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Unir registos — escolha qual MANTER</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">O registo escolhido fica como principal; os restantes são arquivados e as suas ligações (pedidos, contactos, origens, linhas) passam para ele.</p>
          <div className="space-y-2">
            {dlg?.itens.map((it) => (
              <label key={it.id} className="flex items-center gap-2 text-sm p-2 rounded border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid={`keep-${it.id}`}>
                <input type="radio" name="keep" checked={keepId === it.id} onChange={() => setKeepId(it.id)} />
                <span>{it.label}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancelar</Button>
            <Button className="bg-violet-600 hover:bg-violet-700" onClick={doMerge} data-testid="btn-confirmar-uniao">Unir no selecionado</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
